import { Message, MetaConversation } from 'lib-shared-legacy-types';

import { conversationsApi } from 'apis/conversations';
import { getSubject } from 'app/utils/conversationTitle';
import { getMessagesTranscript } from 'assets/app/utils/messages';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';

export type LinkedConversationData = {
  data: {
    body: string;
    subject: string;
    attachments: { url: string; prettyName: string }[];
  } | null;
  isLoading: boolean;
};

export function useLinkedConversationData(
  parentConversation: number | null,
): LinkedConversationData {
  const skip = !parentConversation;

  const { data: convData, isFetching: convFetching } =
    conversationsApi.useReadConversationQuery(
      { csid: parentConversation! },
      { skip },
    );

  const rewrittenHtmlMessages = useAppSelector((state) =>
    state.emailMessages.messages.filter((m) => m.csid === parentConversation),
  );

  const { writeHtmlEmail: hasFFWriteHTMLEmail } = useFeatureFlag([
    'write-html-email',
  ]);

  const isLoading = !skip && convFetching;

  if (!parentConversation || !convData) {
    return { data: null, isLoading };
  }

  const mainConversation = convData.data.history.main_conversation;

  if (mainConversation.id !== parentConversation) {
    return { data: null, isLoading };
  }

  const { events, authors } = mainConversation;
  const messages = events.map(
    (event) =>
      ({
        ...event,
        author: authors[(event as any).author_id] ?? null,
        text: (event as any).text ?? '',
      }) as unknown as Message,
  );

  const metaConversation = {
    conversation: mainConversation,
    messages,
  } as unknown as MetaConversation;

  const { transcript, attachments } = getMessagesTranscript(
    metaConversation,
    rewrittenHtmlMessages,
    hasFFWriteHTMLEmail,
  );

  return {
    data: {
      body: transcript,
      subject:
        getSubject(
          mainConversation.attributes as Parameters<typeof getSubject>[0],
        ) ?? '',
      attachments: attachments.map((a) => ({
        url: a.absolute_uri,
        prettyName: a.name,
      })),
    },
    isLoading,
  };
}
